import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonTypes, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { IconTypes } from '@eika/icon';

import { ErrorCardWrapper } from '../../../../components/shared/ErrorCardWrapper';
import type { EFakturaPermissionId } from '../../../../generated-types';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { eFakturaTrackingIDs } from '../../../../utils/gtm/trackIDs/eFaktura';
import useFormContext from '../../form/useFormContext';
import routes from '../../routes';
import mapTextsFromIssuers from '../../utils/mapper';
import EfakturaSelectListSkeleton from '../EfakturaSelectList/EfakturaSelectListSkeleton';
import ButtonPanelCard from './ButtonPanelCard';
import LinkCardInternalRouting from './LinkCardInternalRouting';
import NoSharedEfakturaInfo from './NoSharedEfakturaInfo';

import './SharedEfakturaList.scss';

const SharedEfakturaList = () => {
    const translated = useTranslation();
    const navigate = useNavigate();
    const { receivedEfakturas, givenEfakturas, deleteReceived } = useFormContext();

    const [showConfirmationDialog, setShowConfirmationDialog] = useState<EFakturaPermissionId | undefined>();

    const refetch = () => {
        receivedEfakturas.refetch?.();
        givenEfakturas.refetch?.();
    };

    const error = receivedEfakturas.error ?? givenEfakturas.error;

    const closeConfirmationDialog = () => {
        setShowConfirmationDialog(undefined);
    };

    return (
        <section className="efaktura-sharing__list-wrapper">
            <div className="efaktura-sharing__list-intro">
                <p>{translated.eFakturaSharing.giveAnotherCustomerAccess}</p>
                <Button
                    buttonType={ButtonTypes.STANDARD_POSITIVE}
                    onClick={() => navigate(routes['choose-person'])}
                    data-track-id={eFakturaTrackingIDs.shareEFaktura.id}
                    data-track-linkname="navigate__start-process"
                    data-testid="share-efaktura-button"
                >
                    {translated.eFakturaSharing.shareYourEfaktura}
                </Button>
            </div>
            {givenEfakturas.isLoading && <EfakturaSelectListSkeleton count={givenEfakturas.data?.length} />}
            {receivedEfakturas.isLoading && <EfakturaSelectListSkeleton count={receivedEfakturas.data?.length} />}
            {error ? (
                <ErrorCardWrapper exception={error}>
                    <ButtonWithSpinner
                        showSpinner={givenEfakturas.isLoading || receivedEfakturas.isLoading}
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                        onClick={refetch}
                    >
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                </ErrorCardWrapper>
            ) : (
                <>
                    <div>
                        <h2>{translated.eFakturaSharing.sharingWith}</h2>
                        {givenEfakturas.data.length === 0 ? (
                            <NoSharedEfakturaInfo
                                headerText={translated.eFakturaSharing.noSharedEfakturaInfo.header}
                                bodyText={translated.eFakturaSharing.noSharedEfakturaInfo.noGivenBody}
                                testID="no-shared-efaktura"
                            />
                        ) : (
                            givenEfakturas.data?.map((given) => (
                                <LinkCardInternalRouting
                                    key={given.id.value}
                                    id={given.id.value}
                                    icon={IconTypes.RADGIVER_DYN}
                                    href={`${routes.update}/${given.id.value}`}
                                    title={given.disponentName}
                                    {...mapTextsFromIssuers(
                                        given.selectedIssuers,
                                        translated.eFakturaSharing.allEfaktura,
                                    )}
                                />
                            ))
                        )}
                    </div>
                    <div>
                        <h2>{translated.eFakturaSharing.sharingWithYou}</h2>
                        {receivedEfakturas.data.length === 0 ? (
                            <NoSharedEfakturaInfo
                                headerText={translated.eFakturaSharing.noSharedEfakturaInfo.header}
                                bodyText={translated.eFakturaSharing.noSharedEfakturaInfo.noReceivedBody}
                            />
                        ) : (
                            receivedEfakturas.data?.map((received) => (
                                <React.Fragment key={received.id.value}>
                                    {received.id.value === showConfirmationDialog?.value ? (
                                        <ConfirmationDialog
                                            title={format(translated.eFakturaSharing.confirmDelete, received.giverName)}
                                            id={`user-delete-${received.id.value}`}
                                            description={translated.eFakturaSharing.confirmDeleteDescription}
                                            footer={
                                                <div>
                                                    <LinkButton
                                                        onClick={closeConfirmationDialog}
                                                        data-track-id={eFakturaTrackingIDs.deleteReceivedEFaktura.id}
                                                        data-track-linkname="navigate__cancel"
                                                    >
                                                        {translated.cancel}
                                                    </LinkButton>
                                                    <ButtonWithSpinner
                                                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                                                        showSpinner={deleteReceived.isLoading}
                                                        showSuccess={deleteReceived.isSuccess}
                                                        onClick={() => deleteReceived.mutate(received.id)}
                                                        data-track-id={eFakturaTrackingIDs.deleteReceivedEFaktura.id}
                                                        data-track-linkname="submit"
                                                    >
                                                        {translated.delete}
                                                    </ButtonWithSpinner>
                                                </div>
                                            }
                                            show={!!showConfirmationDialog}
                                            blueBackground
                                            onExitFocus={closeConfirmationDialog}
                                        />
                                    ) : (
                                        <ButtonPanelCard
                                            id={received.id.value}
                                            icon={IconTypes.RADGIVER_DYN}
                                            actionIcon={IconTypes.SLETT_24}
                                            onClick={() => setShowConfirmationDialog(received.id)}
                                            title={received.giverName}
                                            {...mapTextsFromIssuers(
                                                received.selectedIssuers,
                                                translated.eFakturaSharing.allEfaktura,
                                            )}
                                            data-track-id={eFakturaTrackingIDs.deleteReceivedEFaktura.id}
                                            data-track-linkname="navigate__confirm"
                                        />
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </>
            )}
        </section>
    );
};

export default SharedEfakturaList;
